
import React, { useState } from 'react';
import styles from './productTable.module.css';
import { Downarrow, Leaf, Pdf, Uparrow } from '../../../../Svg';
import { useNavigate } from 'react-router-dom';
import { extractAttributes, stripHtmlTags } from '../../../utils/utils';

const ProductTable = ({ products, addtoCart, quantities, handleQuantityChange, handleCheckboxChange, cartLoading, scheduleLoading, selectedProducts, hide }) => {
    const [expandedProducts, setExpandedProducts] = useState({});
    const [quantityErrors, setQuantityErrors] = useState({});
    const navigate = useNavigate();

    const toggleShowAll = (productId) => {
        setExpandedProducts((prev) => ({
            ...prev,
            [productId]: !prev[productId],
        }));
    };

    const validateQuantity = (productId, value) => {
        const product = products.find(p => String(p._id) === String(productId));
        const minMultiplier = product?.inventory?.minimumMultiplier || 1;
        const qtyAvail = product?.inventory?.qtyAvailable ?? product?.qtyAvailable ?? product?.stock ?? 0;
        const isOutOfStock = product?.status === 'OUT OF STOCK' || qtyAvail <= 0;
        const numValue = Number(value);

        if (isOutOfStock) {
            setQuantityErrors(prev => ({ ...prev, [productId]: 'Out of stock' }));
            return false;
        }

        if (value === '') {
            setQuantityErrors(prev => ({ ...prev, [productId]: '' }));
            return true;
        }

        if (isNaN(numValue)) {
            setQuantityErrors(prev => ({ ...prev, [productId]: 'Please enter a valid number' }));
            return false;
        }

        if (numValue <= 0) {
            setQuantityErrors(prev => ({ ...prev, [productId]: 'Quantity must be greater than 0' }));
            return false;
        }

        if (numValue > qtyAvail) {
            setQuantityErrors(prev => ({ ...prev, [productId]: `Only ${qtyAvail} items available in stock` }));
            return false;
        }

        if (numValue % minMultiplier !== 0) {
            setQuantityErrors(prev => ({ ...prev, [productId]: `Quantity must be in multiples of ${minMultiplier}` }));
            return false;
        }

        setQuantityErrors(prev => ({ ...prev, [productId]: '' }));
        return true;
    };

    const handleQuantityInputChange = (productId, value) => {
        handleQuantityChange(productId, value);
        validateQuantity(productId, value);
    };

    const isQuantityValid = (productId) => {
        const product = products.find(p => String(p._id) === String(productId));
        if (!product) return false;

        const qtyAvail = product?.inventory?.qtyAvailable ?? product?.qtyAvailable ?? product?.stock ?? 0;
        const isOutOfStock = product?.status === 'OUT OF STOCK' || qtyAvail <= 0;
        if (isOutOfStock) return false;

        const quantity = quantities[productId];
        if (!quantity || quantity <= 0) return false;

        const minMultiplier = product?.inventory?.minimumMultiplier || 1;
        const numValue = Number(quantity);

        return !isNaN(numValue) && numValue > 0 && numValue <= qtyAvail && numValue % minMultiplier === 0;
    };


    const handleCompare = () => {
        const ids = selectedProducts.map(item => item._id);
        const jsonIds = JSON.stringify(ids);
        const base64Ids = btoa(jsonIds);
        navigate(`/prodComparision?ids=${base64Ids}`);
    };

    return (
        <div className={styles.productTableContainer}>
            <table className={styles.productTable}>
                <thead className={styles.productTableHead}>
                    <tr>
                        <th className={styles.compare}>Compare</th>
                        <th className={styles.dataSheet}>Data sheet</th>
                        <th className={styles.partNumber}>
                            <div className={styles.headerWithFilter}>
                                <span>Part </span>
                                <div className={styles.sortIcons}>
                                    <Uparrow />
                                    <Downarrow />
                                </div>
                            </div>
                        </th>
                        {hide != "hide" &&
                            <th className={styles.matchType}>Match Type</th>
                        }
                        <th className={styles.manufacturer}>
                            <div className={styles.headerWithFilter}>
                                <span>Manufacturer</span>
                                <div className={styles.sortIcons}>
                                    <Uparrow />
                                    <Downarrow />
                                </div>
                            </div>
                        </th>
                        <th className={styles.stock}>
                            <div className={styles.headerWithFilter}>
                                <span>Available Stock</span>
                                <div className={styles.sortIcons}>
                                    <Uparrow />
                                    <Downarrow />
                                </div>
                            </div>
                        </th>
                        <th className={styles.unitPrice}>Unit Price</th>
                        <th className={styles.gst}>GST</th>
                        <th className={styles.requiredQuantity}>Required Quantity</th>
                        <th className={styles.packageType}>HSN Code</th>
                    </tr>
                </thead>
                <tbody>
                    {products?.map((product, index) => (
                        <tr key={index}>
                            <td className={`${styles.compare}  ${styles.tdBorder}`}>
                                <input type="checkbox"
                                    checked={selectedProducts?.some((p) => p._id === product._id)}
                                    onChange={(e) =>
                                        handleCheckboxChange?.(product, e.target.checked)
                                    }
                                />
                            </td>
                            <td className={`${styles.dataSheet}  ${styles.tdBorder}`}>
                                <div className={styles.dataCol}>
                                    {(product?.productOverview?.datasheetUrl?.[0] || product?.datasheetUrl?.[0] || (typeof product?.datasheet === 'string' ? product.datasheet : null)) ? (
                                        <a href={product?.productOverview?.datasheetUrl?.[0] || product?.datasheetUrl?.[0] || product?.datasheet} target='_blank' rel='noreferrer'>
                                            <Pdf />
                                        </a>
                                    ) : null}
                                    <Leaf />
                                </div>
                            </td>
                            <td className={`${styles.partNumber}  ${styles.tdBorder}`}>
                                <div className={styles.dataRow}>
                                    {(() => {
                                        const rawImg = product?.assets?.featuredImage?.[0] || product?.assets?.image?.[0] || product?.image || '';
                                        const cleanImg = rawImg ? (rawImg.includes('asset.electrowiz.in') ? rawImg.replace('https://asset.electrowiz.in', 'https://electrowiz-ap.s3.ap-south-1.amazonaws.com') : rawImg) : '';
                                        const partNum = product?.productOverview?.productNumber?.[0] || product?.productNumber?.[0] || product?.name || '';
                                        const rawPartDesc = Array.isArray(product?.productOverview?.description) ? product.productOverview.description[0] : (Array.isArray(product?.description) ? product.description[0] : (product?.description || ''));
                                        const partDesc = stripHtmlTags(rawPartDesc);
                                        return (
                                            <>
                                                {cleanImg ? (
                                                    <img src={cleanImg} style={{ width: 50, height: 50, objectFit: 'contain' }} alt={partNum} />
                                                ) : (
                                                    <div style={{ width: 50, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f1f5f9', borderRadius: '4px', fontSize: '20px' }}>⚡</div>
                                                )}
                                                <div className={styles.dataCol}>
                                                    <div className={styles.dataColorChange} onClick={() => navigate(`/productCode/${partNum}`)} style={{ cursor: 'pointer' }}>{partNum}</div>
                                                    <div>{partDesc}</div>
                                                </div>
                                            </>
                                        );
                                    })()}
                                </div>
                            </td>
                            {hide != "hide" &&
                                <td className={`${styles.matchType} ${styles.tdBorder}`}>
                                    <span style={{
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        backgroundColor: product?.matchType === 'Pin to Pin' ? '#e6f4ea' : '#f1f3f4',
                                        color: product?.matchType === 'Pin to Pin' ? '#137333' : '#5f6368',
                                        display: 'inline-block',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {product?.matchType === 'Pin to Pin' ? 'Pin to Pin' : 'Similar'}
                                    </span>
                                </td>
                            }
                            <td className={`${styles.manufacturer}  ${styles.tdBorder}`}>
                                <a href={product?.manufacturerUrl || '#'} className={styles.manufacturer}>
                                    <div className={styles.dataColorChange}>
                                        {Array.isArray(product?.productOverview?.manufacturer) ? product.productOverview.manufacturer[0]?.name : (Array.isArray(product?.manufacturer) ? product.manufacturer[0]?.name : (product?.manufacturer?.name || (typeof product?.manufacturer === 'string' ? product.manufacturer : '')))}
                                    </div>
                                </a>
                            </td>
                            <td className={`${styles.stock}  ${styles.tdBorder}`}>
                                {(() => {
                                    const qtyAvail = product?.inventory?.qtyAvailable ?? product?.qtyAvailable ?? product?.stock ?? 0;
                                    const factoryStockQty = product?.inventory?.factoryStock ?? product?.factoryStock;
                                    const canShipVal = product?.inventory?.canShipImmediately ?? product?.canShipImmediately;
                                    return (
                                        <>
                                            <div style={{ color: '#222222', fontWeight: 700 }}>
                                                {qtyAvail}
                                            </div>
                                            <div className={styles.dataMargin}>{qtyAvail > 0 ? "in stock" : "out of stock"}</div>
                                            {factoryStockQty ?
                                                <>
                                                    <div style={{ color: '#222222', fontWeight: 700 }}>
                                                        {factoryStockQty}
                                                    </div>
                                                    <div className={styles.dataMargin}>factoryStock</div>
                                                </>
                                                : null}
                                            <div>CanShip Immediately: <span style={{ color: '#222222', fontWeight: 700 }}>{canShipVal ? 'YES' : 'NO'}</span> </div>
                                        </>
                                    );
                                })()}
                            </td>
                            <td className={`${styles.unitPrice} ${styles.tdBorder}`}>
                                {product?.quantityTable
                                    ?.slice(
                                        0,
                                        expandedProducts[product?._id]
                                            ? product?.quantityTable?.length
                                            : 2
                                    )
                                    .map((i, index) => (
                                        <div key={index} className={styles.dataMargin}>
                                            {i?.breakQty}: <span style={{ color: '#222222', fontWeight: 700 }}>₹{i?.unitPrice}</span>
                                            <div>
                                                {i?.packaging}
                                            </div>
                                        </div>
                                    ))}
                                {product?.quantityTable?.length > 2 && (
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            toggleShowAll(product?._id);
                                        }}
                                    >
                                        <div className={styles.dataColorChange}>
                                            {expandedProducts[product?._id]
                                                ? "Show Less"
                                                : "More Inventory"}
                                        </div>
                                    </a>
                                )}
                            </td>
                            <td className={`${styles.gst} ${styles.tdBorder}`}>
                                {product?.gst ? `${product?.gst}%` : `_`}
                            </td>
                            {false && (
                            <td className={`${styles.unitPrice} ${styles.tdBorder}`}>
                                {product?.quantityTable
                                    ?.slice(
                                        0,
                                        expandedProducts[product?._id]
                                            ? product?.quantityTable?.length
                                            : 2
                                    )
                                    .map((i, index) => (
                                        <div key={index} className={styles.dataMargin}>
                                            {i?.breakQty}: <span style={{ color: '#222222', fontWeight: 700 }}>₹{i?.finalPrice}</span>
                                            <div>
                                                {i?.packaging}
                                            </div>
                                        </div>
                                    ))}
                                {product?.quantityTable?.length > 2 && (
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            toggleShowAll(product?._id);
                                        }}
                                    >
                                        <div className={styles.dataColorChange}>
                                            {expandedProducts[product?._id]
                                                ? "Show Less"
                                                : "More Inventory"}
                                        </div>
                                    </a>
                                )}
                            </td>
                            )}
                            <td className={`${styles.requiredQuantity}  ${styles.tdBorder}`}>
                                <input
                                    type="number"
                                    placeholder="Enter qty"
                                    className={styles.dataInput}
                                    value={quantities[product?._id] || ""}
                                    onChange={(e) =>
                                        handleQuantityInputChange(product?._id, e.target.value)
                                    }
                                    min="1"
                                    step={product?.inventory?.minimumMultiplier || 1}
                                    disabled={product?.status === 'OUT OF STOCK' || (product?.inventory?.qtyAvailable ?? product?.qtyAvailable ?? product?.stock ?? 0) <= 0}
                                />
                                <div className={styles.spqFlex}>
                                    <span className={styles.spqText}>SPQ {product?.inventory?.minimumMultiplier || 1}</span>
                                    <span className={styles.minimumText}>Min {product?.inventory?.minimumMultiplier || 1}</span>
                                </div>
                                {quantityErrors[product?._id] && (
                                    <div className={styles.errorText}>
                                        {quantityErrors[product?._id]}
                                    </div>
                                )}
                                <div className={styles.dataCol} >
                                    <button
                                        className={styles.addButton}
                                        disabled={!isQuantityValid(product?._id)}
                                        onClick={() => addtoCart('cart', product?._id)}
                                        style={{
                                            cursor: 'pointer',
                                            color: !isQuantityValid(product?._id) ? '#a284e6' : '#512DA8'
                                        }}
                                    >
                                        {cartLoading[product?._id] ? 'Please wait...' : '+ Add to cart'}
                                    </button>
                                    <button
                                        className={styles.scheduleButton}
                                        disabled={!isQuantityValid(product?._id)}
                                        onClick={() => addtoCart('schedule', product?._id)}
                                        style={{
                                            cursor: 'pointer',
                                            color: !isQuantityValid(product?._id) ? '#a284e6' : '#512DA8'
                                        }}
                                    >
                                        {scheduleLoading[product?._id] ? 'Please wait...' : '+ Add to schedule'}
                                    </button>
                                </div>
                                {/* <div className={styles.dataMargin}>
                                    SPQ: {product?.inventory?.minimumMultiplier || 1}
                                </div> */}
                            </td>
                            {['HSN Code'].map((key) => {
                                const val = product?.attributes?.[key]
                                    ?? product?.environmental?.[key]
                                    ?? product?.additionalResources?.[key];
                                const display = Array.isArray(val)
                                    ? val.join(", ")
                                    : (val !== undefined && val !== null && val !== '') ? String(val) : "N/A";
                                return (
                                    <td key={key} className={`${styles.packageType} ${styles.tdBorder}`}>
                                        {display}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            {selectedProducts?.length > 0 ?
                <button
                    className={styles.compareBtn}
                    onClick={handleCompare}
                    disabled={selectedProducts?.length == 1}
                    style={{ background: selectedProducts?.length == 1 ? '#b39beb' : '#512DA8' }}
                >
                    Compare {selectedProducts?.length} products
                </button>
                : null}
        </div>
    );
};

export default ProductTable;




